import { apiRequest, type Schemas } from "./client";

export type Cart = Schemas["Cart"];
export type CartLine = Cart["items"][number];
export type Order = Schemas["Order"];
export type OrderDetail = Schemas["OrderDetail"];
export type PickupPoint = Schemas["Store"]["pickup_point"];
export type BusLine = Schemas["Store"]["bus_lines"][number];
export type SupportChannel = Schemas["Store"]["support_channels"][number];

export type PlaceOrder = {
  fulfilment: Order["fulfilment"];
  payment_method: "bank_transfer" | "cash_on_delivery";
  recipient_name: string;
  phone: string;
  street?: string;
  barangay?: string;
  city?: string;
  province?: string;
  postal_code?: string | null;
  /** Bus terminal pickup only: a key from the store's bus lines, and the customer's terminal. */
  bus_line?: string;
  bus_terminal?: string;
  note: string;
  /** Optional, from an account without an email: saved to it for order updates (PRD 5.6). */
  email_address?: string;
  idempotency_key: string;
  /** The total the customer was shown: the order is refused if a price moved since. */
  expected_total_cents: number;
  /** The basket lines being checked out. Omitted (or empty) orders the whole basket. */
  item_ids?: number[];
};

const data = <T>(response: { data: T }) => response.data;

/** The basket (PRD 5.5). Signed out or in; every change answers with the whole basket. */
export const cartApi = {
  get: () => apiRequest<Schemas["CartRecord"]>("/api/v1/cart").then(data),
  add: (product_listing_id: number, quantity: number) =>
    apiRequest<Schemas["CartRecord"]>("/api/v1/cart/items", { method: "POST", body: { product_listing_id, quantity } }).then(data),
  update: (id: number, quantity: number) => apiRequest<Schemas["CartRecord"]>(`/api/v1/cart/items/${id}`, { method: "PATCH", body: { quantity } }).then(data),
  remove: (id: number) => apiRequest<Schemas["CartRecord"]>(`/api/v1/cart/items/${id}`, { method: "DELETE" }).then(data),
};

/** The signed-in customer's orders (PRD screens 9 to 12). */
export const orderApi = {
  list: () => apiRequest<Schemas["OrderList"]>("/api/v1/orders").then(data),
  get: (reference: string) => apiRequest<Schemas["OrderDetailRecord"]>(`/api/v1/orders/${encodeURIComponent(reference)}`).then(data),
  place: (order: PlaceOrder) => apiRequest<Schemas["OrderDetailRecord"]>("/api/v1/orders", { method: "POST", body: { order } }).then(data),
  cancel: (reference: string) =>
    apiRequest<Schemas["OrderDetailRecord"]>(`/api/v1/orders/${encodeURIComponent(reference)}/cancel`, { method: "POST" }).then(data),
  uploadProof: (reference: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return apiRequest<Schemas["OrderDetailRecord"]>(`/api/v1/orders/${encodeURIComponent(reference)}/payment_proofs`, { method: "POST", body }).then(data);
  },
};

export const storeApi = {
  get: () => apiRequest<Schemas["StoreRecord"]>("/api/v1/store").then(data),
};
